import { CompanyRole } from "@funded-here-interface/common/src/constant/enum";
import { createSlice, PayloadAction } from "@reduxjs/toolkit";

export interface ShareholderDirector {
  role: CompanyRole;
  firstName: string;
  lastName: string;
  email: string;
}

/**
 * Representative state for merchant onboarding
 * Tracks authorized representative, user, and merchant information
 */
export interface RepState {
  // Authorized Representative (company's authorized signatory)
  repFirstName: string | null;
  repLastName: string | null;
  repEmail: string | null;
  repTitle: string | null;

  // Current logged-in user
  userFirstName: string | null;
  userLastName: string | null;

  // Merchant/Company primary contact
  merchantTitle: string | null;
  merchantFirstName: string | null;
  merchantLastName: string | null;

  // Shareholders and Directors
  shareholdersDirectors: ShareholderDirector[];
  numOfShareholdersDirectors: number;

  // Checkboxes
  authChecked: boolean;
  directorChecked: boolean;
}

const initialState: RepState = {
  repFirstName: null,
  repLastName: null,
  repEmail: null,
  userFirstName: null,
  userLastName: null,
  repTitle: null,
  merchantTitle: null,
  merchantFirstName: null,
  merchantLastName: null,
  shareholdersDirectors: [],
  authChecked: false,
  directorChecked: false,
  numOfShareholdersDirectors: 1,
};

/**
 * Generic reducer factory for simple field updates
 * Eliminates duplication for setters that just assign a value
 */
const createFieldSetter = <K extends keyof RepState>(field: K) => {
  return (state: RepState, action: PayloadAction<RepState[K]>) => {
    state[field] = action.payload;
  };
};

const repSlice = createSlice({
  name: "rep",
  initialState,
  reducers: {
    // Representative information setters
    setRepFirstName: createFieldSetter("repFirstName"),
    setRepLastName: createFieldSetter("repLastName"),
    setRepEmail: createFieldSetter("repEmail"),
    setRepTitle: createFieldSetter("repTitle"),

    // User information setters
    setUserFirstName: createFieldSetter("userFirstName"),
    setUserLastName: createFieldSetter("userLastName"),

    // Merchant information setters
    setMerchantTitle: createFieldSetter("merchantTitle"),
    setMerchantFirstName: createFieldSetter("merchantFirstName"),
    setMerchantLastName: createFieldSetter("merchantLastName"),

    // Shareholder/Director setters
    setShareholderDirector: createFieldSetter("shareholdersDirectors"),
    setNumOfShareholdersDirectors: createFieldSetter("numOfShareholdersDirectors"),

    // Checkbox setters
    setAuthChecked: createFieldSetter("authChecked"),
    setDirectorChecked: createFieldSetter("directorChecked"),

    /**
     * Bulk update multiple fields at once
     * Useful for hydrating state from API response
     */
    hydrateRepState: (state, action: PayloadAction<Partial<RepState>>) => {
      return { ...state, ...action.payload };
    },

    /**
     * Reset to initial state
     */
    resetRepState: () => initialState,
  },
});

export const {
  setRepFirstName,
  setRepLastName,
  setRepEmail,
  setUserFirstName,
  setUserLastName,
  setRepTitle,
  setMerchantTitle,
  setShareholderDirector,
  setMerchantFirstName,
  setMerchantLastName,
  setAuthChecked,
  setDirectorChecked,
  setNumOfShareholdersDirectors,
  hydrateRepState,
  resetRepState,
} = repSlice.actions;

export default repSlice.reducer;
